import { DeepOmit, notEmpty } from './../../lib/helpers/apollo';
import { gql } from '@apollo/client';
import { useCallback, useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { filterTypenameFromArray } from '../../lib/helpers/apollo';
import {
	ContractStatus,
	InstallmentStatus,
	InvoiceStatus,
	ProposalStatus,
	UserType,
	DocumentFollowupActionsContactFragment,
	DocumentFollowupActionsContactForGetContractWhereFragment,
} from '#types';
import { FlattenedUser } from '../../types/user';
import PermissionsModal, { VendorForPermissions } from '../PermissionsModal';
import { GuestRegisterPromptModalProps } from './GuestRegisterPromptModal';
import PayInvoicesModal, { PayInvoicesModalProps } from './PayInvoicesModal';
import rpcShared from '@rockpapercoin/rpc-shared';
import SignContractsModal, {
	SignContractsModalProps,
} from './SignContractsModal';
import AcceptProposalsModal, {
	AcceptProposalsModalProps,
} from './AcceptProposalsModal';

export enum Modals {
	PlannerPermissions = 'PlannerPermissions',
	GuestSignUp = 'GuestSignUp',
	SignContracts = 'SignContracts',
	PayInvoices = 'PayInvoices',
	AcceptProposals = 'AcceptProposals',
}

type PlannerPermissionsModalType = {
	type: Modals.PlannerPermissions;
	data: Parameters<typeof PermissionsModal>[0];
};

type GuestSignUpType = {
	type: Modals.GuestSignUp;
	data: Omit<GuestRegisterPromptModalProps, 'open'>;
};

type PayInvoicesType = {
	type: Modals.PayInvoices;
	data: Omit<PayInvoicesModalProps, 'open'>;
};

type SignContractsType = {
	type: Modals.SignContracts;
	data: Omit<SignContractsModalProps, 'open'>;
};

type AcceptProposalsType = {
	type: Modals.AcceptProposals;
	data: Omit<AcceptProposalsModalProps, 'open'>;
};

type ModalType =
	| PlannerPermissionsModalType
	| GuestSignUpType
	| PayInvoicesType
	| SignContractsType
	| AcceptProposalsType;

const documentFollowupActionsInvoiceFragment = gql`
	fragment documentFollowupActionsInvoice on Invoice {
		id
		status
		...payInvoicesModalInvoice
	}
	${ PayInvoicesModal.fragments.payInvoicesModalInvoiceFragment }
`;

const documentFollowupActionsInvoiceForGetContractWhereFragment = gql`
	fragment documentFollowupActionsInvoiceForGetContractWhere on InvoiceForGetContractWhere {
		id
		status
		...payInvoicesModalInvoiceForGetContractWhere
	}
	${ PayInvoicesModal.fragments
		.payInvoicesModalInvoiceForGetContractWhereFragment }
`;

export const documentFollowupActionsContactFragment = gql`
	fragment documentFollowupActionsContact on Contact {
		id
		customerSignatures {
			id
			signature {
				id
				signingDate
				signer {
					id
				}
				contract {
					id
					status
					dueDate
				}
			}
		}
		invoices {
			...documentFollowupActionsInvoice
		}
		customer {
			id
			clientUser {
				id
				plannerOrgCanPayInvoices
				plannerOrgCanSignContracts
				assignedPlanner {
					id
					organization {
						id
						users {
							id
							role
						}
					}
				}
			}
		}
		vendor {
			...permissionsModalOrganization
		}
		...acceptProposalsModalContact
		...signContractsModalContact
	}
	${ documentFollowupActionsInvoiceFragment }
	${ SignContractsModal.fragments.signContractsModalContactFragment }
	${ AcceptProposalsModal.fragments.acceptProposalsModalContactFragment }
	${ PermissionsModal.fragments.permissionsModalOrganizationFragment }
`;

export const documentFollowupActionsContactForGetContractWhereFragment = gql`
	fragment documentFollowupActionsContactForGetContractWhere on ContactForGetContractWhere {
		id
		customerSignatures {
			id
			signature {
				id
				signingDate
				signer {
					id
				}
				contract {
					id
					status
					dueDate
				}
			}
		}
		invoices {
			...documentFollowupActionsInvoiceForGetContractWhere
		}
		customer {
			id
			clientUser {
				id
				plannerOrgCanPayInvoices
				plannerOrgCanSignContracts
				assignedPlanner {
					id
					organization {
						id
						users {
							id
							role
							user {
								id
							}
						}
					}
				}
			}
		}
		vendor {
			...permissionsModalOrganizationForGetContractWhere
		}
		...acceptProposalsModalContactForGetContractWhere
		...signContractsModalContactForGetContractWhere
	}
	${ documentFollowupActionsInvoiceForGetContractWhereFragment }
	${ SignContractsModal.fragments
		.signContractsModalContactForGetContractWhereFragment }
	${ AcceptProposalsModal.fragments
		.acceptProposalsModalContactForGetContractWhereFragment }
	${ PermissionsModal.fragments
		.permissionsModalOrganizationForGetContractWhereFragment }
`;

type ModalChainedAction = ( () => any ) | undefined;

export type UseDocumentFollowupActionsProps = {
	documentType: 'proposal' | 'contract' | 'invoice';
	documentId: string;
	contact?:
		| DocumentFollowupActionsContactFragment
		| DocumentFollowupActionsContactForGetContractWhereFragment
		| null;
	requestingUserBaseId: string;
	requestingUserIsGuest: boolean;
};

const contactIsSignContractsModalContactFragment = (
	contact:
		| UseDocumentFollowupActionsProps['contact']
		| SignContractsModalProps['contact']
): contact is SignContractsModalProps['contact'] =>
	!!contact && 'customerSignatures' in contact;

type ContactType = DeepOmit<
	| DocumentFollowupActionsContactFragment
	| DocumentFollowupActionsContactForGetContractWhereFragment
>;

export const useDocumentFollowupActions = ( {
	documentType,
	documentId,
	contact,
	requestingUserIsGuest,
	requestingUserBaseId,
}: UseDocumentFollowupActionsProps ) => {
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const [ modal, setModal ] = useState<ModalType | undefined>();

	const viewingUserIsCustomer = contact
		? rpcShared.contacts.isCustomer( contact, requestingUserBaseId )
		: false;
	const orgUser =
		user?.isLoggedIn && user.userType === UserType.OrgUser
			? user
			: contact
				? rpcShared.contacts.getAPPAOrgUserByBaseUser(
					contact as ContactType,
					requestingUserBaseId
			  )
				: undefined;
	const viewingUserIsAPPA =
		orgUser && contact
			? rpcShared.contacts.isAssignedPlannerOrAdminOfCustomersPlannerOrg(
				contact,
				orgUser
			  )
			: false;
	const userIsOboAndCanSignContracts =
		viewingUserIsAPPA &&
		contact?.customer.clientUser?.plannerOrgCanSignContracts;
	const userIsOboAndCanPayInvoices =
		( viewingUserIsAPPA &&
			contact?.customer.clientUser?.plannerOrgCanPayInvoices ) ||
		false;
	const userIsOboAndCanAcceptProposals = viewingUserIsAPPA;

	// Invoices that have unpaid installments
	const actionableInvoices = useMemo(
		() =>
			contact?.invoices
				? filterTypenameFromArray( contact.invoices ).filter(
					// ACH-payed installments are "Pending" and so the invoice status is still Sent despite kinda being paid already
					( invoice ) =>
						invoice.id !== documentId &&
							[ InvoiceStatus.Sent, InvoiceStatus.PartiallyPaid ].includes(
								invoice.status
							) &&
							invoice.paymentInstallments?.some(
								( installment ) => installment.status === InstallmentStatus.Unpaid
							)
				  )
				: [],
		[ contact?.invoices, documentId ]
	);

	const contracts =
		contact && 'customerSignatures' in contact && contact.customerSignatures
			? contact.customerSignatures
				.filter(
					( customerSignature ) =>
						customerSignature.signature.contract?.id !== documentId
				)
				.map( ( customerSignature ) => ( {
					id: customerSignature.signature.contract?.id,
					status: customerSignature.signature.contract?.status,
					dueDate: customerSignature.signature.contract?.dueDate,
					signatures: [
						{
							id: customerSignature.signature.id,
							signingDate: customerSignature.signature.signingDate,
							signer: {
								id: customerSignature.signature.signer?.id,
							},
						},
					],
				} ) )
				.filter( notEmpty )
			: undefined;

	// Signable contracts that are not overdue
	const actionableContracts = useMemo(
		() =>
			contracts
				? filterTypenameFromArray( contracts ).filter(
					( contract ) =>
						( contract.status === ContractStatus.Sent ||
								contract.status === ContractStatus.PartiallySigned ) &&
							contract.dueDate &&
							new Date( contract.dueDate ) > new Date() &&
							contract.signatures.some( ( signature ) => !signature.signingDate )
				  )
				: [],
		[ contracts ]
	);

	// Proposals that have not yet been accepted
	const actionableProposals = useMemo(
		() =>
			contact?.proposals?.filter(
				( proposal ) =>
					proposal.status === ProposalStatus.Sent && proposal.id !== documentId
			) || [],
		[ contact?.proposals, documentId ]
	);

	// in all cases for ClientUsers or Orguser Customers - modal shown or closed - do this
	const finalAction = useCallback( () => {
		setModal( undefined );
		return false;
	}, [] );

	const clientUser = viewingUserIsCustomer
		? contact?.customer.clientUser
		: undefined;

	const canShowPermissionsModal =
		( !clientUser?.assignedPlanner?.id &&
			contact?.vendor?.services &&
			filterTypenameFromArray( contact.vendor.services ).some(
				( service ) => service?.name === 'Event Planner'
			) ) ||
		false;

	const canShowAcceptProposalsModal =
		( ( viewingUserIsCustomer || userIsOboAndCanAcceptProposals ) &&
			actionableProposals.length > 0 ) ||
		false;

	const canShowSignContractsModal =
		( ( viewingUserIsCustomer || userIsOboAndCanSignContracts ) &&
			actionableContracts.length > 0 ) ||
		false;

	const canShowPayInvoicesModal =
		( ( viewingUserIsCustomer || userIsOboAndCanPayInvoices ) &&
			actionableInvoices.length > 0 ) ||
		false;

	const modalTitle =
		documentType === 'proposal'
			? 'Proposal accepted'
			: documentType === 'contract'
				? 'Contract signed'
				: 'Installment paid';

	// A helper to show this modal, since we never need to chain something after this
	const showAcceptProposalsModal = useCallback(
		(
			contact: AcceptProposalsModalProps['contact'],
			onClose: ModalChainedAction = finalAction
		) => {
			setModal( {
				type: Modals.AcceptProposals,
				data: {
					title: modalTitle,
					onClose,
					onNavigate: finalAction,
					contact,
					excludedDocumentId: documentId,
					description:
						contact?.customer.id !== requestingUserBaseId
							? 'Your client still needs to accept these proposals to make it official.'
							: 'You still need to accept these proposals to make it official.',
				},
			} );
			return true;
		},
		[
			finalAction,
			modalTitle,
			documentId,
			requestingUserBaseId
		]
	);

	// A helper to show this modal, since we never need to chain something after this
	const showPayInvoicesModal = useCallback(
		( viewingUserId: string, onClose: ModalChainedAction = finalAction ) => {
			setModal( {
				type: Modals.PayInvoices,
				data: {
					title: modalTitle,
					onClose,
					onNavigate: finalAction,
					contact,
					excludedDocumentId: documentId,
					paymentText:
						contact?.customer.id !== viewingUserId
							? 'Your client still needs to submit a payment to the vendor.'
							: 'Take care of your invoice payment.',
				},
			} );
			return true;
		},
		[
			contact,
			finalAction,
			modalTitle,
			documentId
		]
	);

	const showSignContractsModal = useCallback(
		(
			contact: SignContractsModalProps['contact'],
			onClose: ModalChainedAction = finalAction
		) => {
			setModal( {
				type: Modals.SignContracts,
				data: {
					title: modalTitle,
					onClose,
					onNavigate: finalAction,
					contact,
					excludedDocumentId: documentId,
				},
			} );
			return true;
		},
		[
			finalAction,
			modalTitle,
			documentId
		]
	);

	const showGuestRegisterPromptModal = useCallback(
		( onClose: ModalChainedAction = finalAction ) => {
			setModal( {
				type: Modals.GuestSignUp,
				data: {
					onClose,
				},
			} );
			return true;
		},
		[ finalAction ]
	);

	const showPlannerPermissionsModal = useCallback(
		(
			contact: { id: string; vendor: VendorForPermissions },
			clientUser: { id: string },
			onClose: ModalChainedAction = finalAction
		) => {
			setModal( {
				type: Modals.PlannerPermissions,
				data: {
					clientUserId: clientUser.id,
					contactId: contact.id,
					vendor: contact.vendor,
					onCancel: onClose,
					onConnect: async () => {
						finalAction();
					},
				},
			} );
			return true;
		},
		[ finalAction ]
	);

	/* Priority goes:
	- if there are more documents from this vendor of "this type", show those
	- if there are documents of "this type", or if they close it and there's contracts from this vendor to show, show contracts modal
	- if there are documents of "this type", or if they close it and there's invoices from this vendor to show, show invoices modal
	- if there are documents of "this type", or if they close it and there's proposals from this vendor to show, show proposals modal
	and of course we don't loop, so if we show a modal and the user closes it then we don't show it again
	*/
	const takeAction = useCallback(
		(
			currentDocumentType: 'proposal' | 'contract' | 'invoice',
			dismissedModalTypes: (
				| 'proposal'
				| 'contract'
				| 'invoice'
				| 'permissions'
				| 'guestRegister'
			)[]
		): ( () => boolean ) | undefined => {
			const thisDocumentType = documentType;
			const canShowThisDocumentTypeModal =
				thisDocumentType === 'proposal'
					? canShowAcceptProposalsModal
					: thisDocumentType === 'contract'
						? canShowSignContractsModal
						: canShowPayInvoicesModal;
			const thisChainedAction = () =>
				takeAction( currentDocumentType, [ ...dismissedModalTypes, thisDocumentType, ] )?.();
			const thisAction =
				thisDocumentType === 'proposal'
					? () => showAcceptProposalsModal( contact, thisChainedAction )
					: thisDocumentType === 'contract' &&
					  contactIsSignContractsModalContactFragment( contact )
						? () => showSignContractsModal( contact, thisChainedAction )
						: thisDocumentType === 'invoice' && requestingUserBaseId
							? () => showPayInvoicesModal( requestingUserBaseId, thisChainedAction )
							: undefined;
			if (
				!dismissedModalTypes.includes( documentType ) &&
				canShowThisDocumentTypeModal &&
				thisAction
			) {
				return thisAction;
			} else if (
				thisDocumentType !== 'contract' &&
				!dismissedModalTypes.includes( 'contract' ) &&
				canShowSignContractsModal &&
				contactIsSignContractsModalContactFragment( contact )
			) {
				return () =>
					showSignContractsModal( contact, () =>
						takeAction( currentDocumentType, [ ...dismissedModalTypes, 'contract', ] )?.()
					);
			} else if (
				thisDocumentType !== 'invoice' &&
				!dismissedModalTypes.includes( 'invoice' ) &&
				canShowPayInvoicesModal &&
				requestingUserBaseId
			) {
				return () =>
					showPayInvoicesModal( requestingUserBaseId, () =>
						takeAction( currentDocumentType, [ ...dismissedModalTypes, 'invoice', ] )?.()
					);
			} else if (
				thisDocumentType !== 'proposal' &&
				!dismissedModalTypes.includes( 'proposal' ) &&
				canShowAcceptProposalsModal &&
				contact
			) {
				return () =>
					showAcceptProposalsModal( contact, () =>
						takeAction( currentDocumentType, [ ...dismissedModalTypes, 'proposal', ] )?.()
					);
			} else if (
				!dismissedModalTypes.includes( 'permissions' ) &&
				canShowPermissionsModal &&
				contact &&
				clientUser
			) {
				const { __typename: _, services, ...vendorRest } = contact.vendor;
				return () =>
					showPlannerPermissionsModal(
						{
							id: contact.id,
							vendor: {
								...vendorRest,
								services: filterTypenameFromArray(
									services?.filter( notEmpty ) || []
								),
							},
						},
						clientUser
					);
			} else if (
				!dismissedModalTypes.includes( 'guestRegister' ) &&
				requestingUserIsGuest &&
				!user?.isLoggedIn
			) {
				return () => showGuestRegisterPromptModal();
			} else {
				return () => finalAction();
			}
		},
		[
			documentType,
			canShowAcceptProposalsModal,
			canShowSignContractsModal,
			canShowPayInvoicesModal,
			contact,
			requestingUserBaseId,
			canShowPermissionsModal,
			clientUser,
			requestingUserIsGuest,
			user,
			showAcceptProposalsModal,
			showSignContractsModal,
			showPayInvoicesModal,
			showPlannerPermissionsModal,
			showGuestRegisterPromptModal,
			finalAction,
		]
	);

	const onSuccess = useCallback(
		async () => takeAction( documentType, [] )?.(),
		[ documentType, takeAction ]
	);

	// add a hotkey to aid in testing that launches this modal
	useEffect( () => {
		const handleKeyDown = ( event: KeyboardEvent ) => {
			// When Ctrl is held and a key is pressed, event.key is usually 'Dead' on many keyboards.
			if ( event.ctrlKey && event.code === 'Backquote' ) {
				onSuccess();
			}
		};
		window.addEventListener( 'keydown', handleKeyDown );
		return () => window.removeEventListener( 'keydown', handleKeyDown );
	}, [ onSuccess ] );

	return {
		modal,
		onSuccess,
	};
};
